import { createContext, useCallback, useContext, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const Ctx = createContext(() => Promise.resolve(false));

export function StepUpProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const resolver = useRef(null);
  const requireStepUp = useCallback(async () => {
    try { const { data } = await api.get("/auth/step-up/status"); if (data.active) return true; if (!data.pin_set) { toast.error("Set the admin master PIN under Company → Secrets vault first"); return false; } }
    catch (err) { toast.error(formatApiError(err)); return false; }
    return new Promise((resolve) => { resolver.current = resolve; setPin(""); setOpen(true); });
  }, []);
  const finish = (ok) => { setOpen(false); resolver.current?.(ok); resolver.current = null; };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/step-up", { pin }); toast.success("Unlocked for 10 minutes"); finish(true); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Ctx.Provider value={requireStepUp}>
      {children}
      <Dialog open={open} onOpenChange={(o) => !o && finish(false)}>
        <DialogContent className="max-w-sm" data-testid="step-up-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-amber-600" />Confirm it's you</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-slate-600">This action is sensitive. Enter the admin master PIN to unlock protected actions for 10 minutes.</p>
            <div><Label htmlFor="step-up-pin">Admin PIN</Label><Input id="step-up-pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} autoFocus className="font-mono tracking-widest" data-testid="step-up-pin-input" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => finish(false)}>Cancel</Button><Button type="submit" disabled={busy || pin.length < 4} className="bg-blue-700 hover:bg-blue-800" data-testid="step-up-submit">{busy ? "Checking…" : "Unlock"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export const useStepUp = () => useContext(Ctx);
